import VacancyApplicationMeta from './VacancyApplicationMeta'
import VacancyLocation from './VacancyLocation'
import VacancyRole from './VacancyRole'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import unescape from 'lodash/unescape'
import { PositionType } from '@/constants/vacancies/positionTypes'
import { Stages } from '@/constants/vacancies/statuses'
import { VacancyApplicationStatuses } from '@/constants/vacancies/vacancyApplicationStatuses'
import File from '@/models-ts/File'
import Skill from '@/models-ts/Skill'
import UserInfo from '@/models-ts/user/UserInfo'
import { parseSlug } from '@/utils-ts/parser'
import { stripDescriptionTags } from '@/utils-ts/strings'

export default class VacancyApplication {
  id: number
  isRead: boolean
  freelancer: UserInfo & { position: string }
  customer: UserInfo
  status: VacancyApplicationStatuses
  comment: string
  created_at: string
  updated_at: string
  cvFile?: File
  meta?: VacancyApplicationMeta | null
  vacancyId: number
  vacancy: {
    id: number
    slug: string
    // status: JobStatus
    stage: Stages
    updated_at: string
    published_at?: string
    name: string
    description: string
    locations: VacancyLocation[]
    positionOffice: boolean
    positionRemote: boolean
    positionType: PositionType | null
    payments_is_crypto: number
    primaryRole: VacancyRole
    salary_type: number
    salary_from: number
    salary_to: number
    skills: Array<Skill>
  }

  constructor(data: Partial<VacancyApplication>) {
    Object.assign(this, cloneDeep(data))
  }

  static fromServer(data: LX.VacancyApplication) {
    const freelancer = data.relations.Freelancer
    const customer = data.relations.Customer
    const vacancy = data.relations.Vacancy
    const files = data.relations.File || []
    const primaryRole = get(vacancy.relations, 'PrimaryRoles[0]', { id: null, name: null })
    let cvFile
    if (files.length) {
      cvFile = File.fromServer(files[0])
    }
    return new VacancyApplication({
      id: data.id,
      isRead: Boolean(data.is_read),
      status: data.status,
      comment: data.comment,
      created_at: data.created_at,
      updated_at: data.updated_at,
      cvFile,
      vacancyId: data.vacancy_id,
      freelancer: {
        ...UserInfo.fromServer(freelancer),
        position: freelancer.profile.position || '',
      },
      customer: UserInfo.fromServer(customer),
      meta: data.tabs_meta,
      vacancy: {
        id: vacancy.id || 0,
        slug: parseSlug(vacancy?.slug),
        updated_at: vacancy.updated_at,
        published_at: vacancy.published_at,
        // stage: data.relations?.Job.stage,
        // status: data.relations?.Job.status,
        name: unescape(vacancy?.name),
        description: stripDescriptionTags(vacancy.description, { stripLinks: true }),
        locations: (vacancy.relations.Location || []).map(VacancyLocation.fromServer),
        positionOffice: Boolean(vacancy.position_office),
        positionRemote: Boolean(vacancy.position_remote),
        positionType: vacancy.position_type,
        payments_is_crypto: vacancy.payments_is_crypto,
        primaryRole: VacancyRole.fromServer(primaryRole),
        salary_type: vacancy.salary_type,
        salary_from: Number(vacancy.salary_from || 0),
        salary_to: Number(vacancy.salary_to || 0),
        skills: (vacancy.relations.Skill || []).map(Skill.fromServer),
        stage: vacancy.stage,
      },
    })
  }

  get hasCompensation() {
    return this.vacancy.salary_from > 0 || this.vacancy.salary_to > 0
  }

  get remoteInfo() {
    const vacancy = this.vacancy
    if (vacancy.positionRemote) {
      return vacancy.positionOffice ? 'Hybrid' : 'Remote'
    }
    return 'Office'
  }

  get sortField() {
    return this.updated_at || this.created_at
  }
}
